import styled from "@emotion/styled";
import {
  BORDER_RADIUS_DEFAULT,
  PLACEHOLDER_TEXT_COLOR,
  COLOR_TERTIARY,
  HEADER_FONT_FAMILY,
  FONT_SIZE_MEDIUM,
  COLOR_ERROR,
  COLOR_APP_TEXT_LIGHT,
  COLOR_APP_TEXT,
  COLOR_DROPDOWN_TEXT_SELECTED,
  COLOR_MENU_HOVER,
  BOX_SHADOW_LARGE,
  COLOR_INPUT_DEFAULT_BORDER,
  COLOR_INPUT_BORDER_FOCUSED,
  COLOR_INPUT_BORDER_HOVER,
} from "../theme";
import Select, {
  components,
  OptionProps,
  SingleValueProps,
  CSSObjectWithLabel,
  type SelectInstance,
} from "react-select";
import {
  CSSProperties,
  useCallback,
  useRef,
  type KeyboardEvent,
  type MutableRefObject,
} from "react";
import { InputWrapper, ErrorText } from "./InputField";
import { css } from "@emotion/react";

export interface DropDownOptionNoIcon {
  label: string;
  value: string;
}

export interface DropDownOptionWithIcon {
  label: string;
  value: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

export type DropDownOption = DropDownOptionNoIcon | DropDownOptionWithIcon;

const TYPEAHEAD_RESET_MS = 800;

const useDropdownTypeahead = (
  options: DropDownOption[],
  selectedValue: string,
  onOptionSelect?: (option: DropDownOption) => void,
  isDisabled?: boolean,
  selectRef?: MutableRefObject<SelectInstance<
    DropDownOption | undefined
  > | null>,
) => {
  const typeaheadStateRef = useRef({ buffer: "", lastKeyTime: 0 });

  return useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (isDisabled || !onOptionSelect) {
        return;
      }

      if (
        event.key.length !== 1 ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }

      const key = event.key;
      const now = Date.now();
      const { buffer, lastKeyTime } = typeaheadStateRef.current;
      const isExpired = now - lastKeyTime > TYPEAHEAD_RESET_MS;
      const nextBuffer = isExpired ? key : buffer + key;
      const startIndex = options.findIndex(
        (option) => option.value === selectedValue,
      );

      const orderedOptions =
        startIndex >= 0
          ? [
              ...options.slice(startIndex + 1),
              ...options.slice(0, startIndex + 1),
            ]
          : options;

      const searchCandidates =
        nextBuffer.length > 1 ? [nextBuffer, key] : [nextBuffer];

      for (const searchTerm of searchCandidates) {
        const normalizedSearch = searchTerm.toLocaleLowerCase();
        const match = orderedOptions.find((option) =>
          option.label.toLocaleLowerCase().startsWith(normalizedSearch),
        );

        if (match) {
          typeaheadStateRef.current = {
            buffer: searchTerm,
            lastKeyTime: now,
          };

          const selectInstance = selectRef?.current;
          if (selectInstance) {
            const internalSelect = selectInstance as unknown as {
              getFocusedOptionId: (option: DropDownOption) => string | null;
              setState: (
                state: Partial<{
                  focusedOption: DropDownOption;
                  focusedOptionId: string | null;
                  focusedValue: null;
                }>,
              ) => void;
              scrollToFocusedOptionOnUpdate: boolean;
            };
            const focusedOptionId = internalSelect.getFocusedOptionId(match);
            internalSelect.scrollToFocusedOptionOnUpdate = true;
            internalSelect.setState({
              focusedOption: match,
              focusedOptionId,
              focusedValue: null,
            });
          }

          if (match.value !== selectedValue) {
            onOptionSelect(match);
          }

          event.preventDefault();
          return;
        }
      }

      typeaheadStateRef.current = {
        buffer: "",
        lastKeyTime: now,
      };
    },
    [isDisabled, onOptionSelect, options, selectRef, selectedValue],
  );
};

const guardDropDownOptionsWithIcon = (
  options: DropDownOption[],
): options is DropDownOptionWithIcon[] => {
  return options.every((option) => "Icon" in option);
};

const baseSelectStyles = css`
  /* Main container - let it size naturally but pass width to menu */
  & {
    position: relative;
  }

  .styled-select__control {
    height: 56px;
    padding: 16px;
    border-radius: ${BORDER_RADIUS_DEFAULT};
    font-size: ${FONT_SIZE_MEDIUM};
    font-family: ${HEADER_FONT_FAMILY};
    background-color: ${COLOR_TERTIARY};
    border: ${`1px solid ${COLOR_INPUT_DEFAULT_BORDER}`};
    box-shadow: none;
    cursor: pointer;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    box-sizing: border-box;
    min-width: 0; /* Allow flexbox to shrink */
  }

  .styled-select__value-container {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0; /* Allow flexbox to shrink */
  }

  .styled-select__single-value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  .styled-select__control:hover {
    border: 1px solid ${COLOR_INPUT_BORDER_HOVER};
  }

  .styled-select__control--is-focused {
    border: 1px solid ${COLOR_INPUT_BORDER_FOCUSED};
  }

  .styled-select__placeholder {
    color: ${PLACEHOLDER_TEXT_COLOR};
  }

  .styled-select__indicator-separator {
    display: none;
  }

  .styled-select__dropdown-indicator {
    padding: 0 8px;
    color: ${COLOR_APP_TEXT_LIGHT};
    flex-shrink: 0;
  }

  .styled-select__dropdown-indicator:hover {
    color: ${COLOR_APP_TEXT};
  }

  /* Menu positioning - force it to match control width exactly */
  .styled-select__menu {
    z-index: 20;
    background-color: ${COLOR_TERTIARY};
    border-radius: ${BORDER_RADIUS_DEFAULT};
    overflow: hidden;
    box-shadow: ${BOX_SHADOW_LARGE};
    margin-top: 4px;
    box-sizing: border-box;
    /* Match the exact width of the control */
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }

  .styled-select__menu-list {
    background-color: ${COLOR_TERTIARY};
    padding: 0;
    box-shadow: ${BOX_SHADOW_LARGE};
    font-size: ${FONT_SIZE_MEDIUM};
    font-family: ${HEADER_FONT_FAMILY};
    box-sizing: border-box;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }

  .styled-select__option {
    padding: 16px 24px;
    display: flex;
    align-items: center;
    cursor: pointer;
    width: 100%;
    box-sizing: border-box;

    &--is-focused {
      background-color: ${COLOR_MENU_HOVER};
    }

    &--is-selected {
      background-color: ${COLOR_TERTIARY};
      color: ${COLOR_DROPDOWN_TEXT_SELECTED};
    }

    &:hover {
      background-color: ${COLOR_MENU_HOVER};
    }
  }
`;
interface StyledSelectProps {
  hasError?: boolean;
  bgColor?: string;
}

const StyledSelectWithIcon = styled(
  Select<DropDownOptionWithIcon | undefined, false>,
)<StyledSelectProps>`
  ${baseSelectStyles}
  .styled-select__control {
    background-color: ${({ bgColor }) => bgColor ?? COLOR_TERTIARY};
    ${({ hasError }) =>
      hasError && `box-shadow: inset 0 0 0 1px ${COLOR_ERROR};`}
  }

  .styled-select__menu {
    background-color: ${({ bgColor }) => bgColor ?? COLOR_TERTIARY};
  }

  .styled-select__menu-list {
    background-color: ${({ bgColor }) => bgColor ?? COLOR_TERTIARY};
  }
`;

const StyledSelect = styled(
  Select<DropDownOptionNoIcon, false>,
)<StyledSelectProps>`
  ${baseSelectStyles}
  .styled-select__control {
    background-color: ${({ bgColor }) => bgColor ?? COLOR_TERTIARY};
    ${({ hasError }) =>
      hasError && `box-shadow: inset 0 0 0 1px ${COLOR_ERROR};`}
  }

  .styled-select__menu {
    background-color: ${({ bgColor }) => bgColor ?? COLOR_TERTIARY};
  }

  .styled-select__menu-list {
    background-color: ${({ bgColor }) => bgColor ?? COLOR_TERTIARY};
  }
`;

// Simple wrapper component to apply width constraints
const SelectWrapper: React.FC<{
  children: React.ReactNode;
  selectStyles?: CSSProperties;
}> = ({ children, selectStyles }) => (
  <div style={{ minWidth: "100px", ...selectStyles }}>{children}</div>
);

interface DropdownProps {
  options: DropDownOption[];
  selectedValue: string;
  onChange?: (value: string) => void;
  dataTestid: string;
  /**
   * Inline style overrides applied to react-select parts.
   * These styles are merged into the following sections:
   * control, menu, menuList, valueContainer and singleValue.
   */
  styles?: CSSProperties;
  /**
   * Background colour applied to the dropdown control and its menu. If not
   * provided, defaults to COLOR_TERTIARY (white).
   */
  backgroundColor?: string;
  errorMessage?: string | null;
  placeholder?: string;
  isDisabled?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  selectedValue,
  onChange,
  dataTestid,
  styles,
  backgroundColor,
  errorMessage,
  placeholder,
  isDisabled,
}) => {
  const selectInstanceRef = useRef<SelectInstance<
    DropDownOption | undefined
  > | null>(null);

  const handleOptionSelect = useCallback(
    (option: DropDownOption) => {
      onChange?.(option.value);
    },
    [onChange],
  );

  const handleTypeaheadKeyDown = useDropdownTypeahead(
    options,
    selectedValue,
    handleOptionSelect,
    isDisabled,
    selectInstanceRef,
  );

  // Simple style composition helper that excludes width (we'll apply width to container)
  const composeStyles = (base: CSSObjectWithLabel): CSSObjectWithLabel => {
    // Extract width-related properties to exclude them from react-select internal styling
    // These are intentionally excluded and applied to the wrapper component instead
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
      width, // Applied to wrapper instead of react-select internals
      minWidth: styleMinWidth, // Applied to wrapper instead of react-select internals
      maxWidth, // Applied to wrapper instead of react-select internals
      ...otherStyles
    } = styles ?? {};
    /* eslint-enable @typescript-eslint/no-unused-vars */
    return {
      ...base,
      ...otherStyles,
      minWidth: "100px", // Prevent text cropping on mobile
    };
  };

  // this if else is a little ugly, but the typing if you try to do it all in one gets really bad
  if (guardDropDownOptionsWithIcon(options)) {
    const selectedOption = options.find(
      (option) => option.value === selectedValue,
    );
    return (
      <InputWrapper data-testid={dataTestid}>
        <SelectWrapper selectStyles={styles ?? {}}>
          <StyledSelectWithIcon
            {...(backgroundColor ? { bgColor: backgroundColor } : {})}
            hasError={!!errorMessage}
            ref={
              selectInstanceRef as MutableRefObject<SelectInstance<
                DropDownOptionWithIcon | undefined
              > | null>
            }
            styles={{
              control: composeStyles,
              menu: composeStyles,
              menuList: composeStyles,
              valueContainer: composeStyles,
              singleValue: composeStyles,
            }}
            isSearchable={false}
            components={{ Option: IconOption, SingleValue: IconSingleValue }}
            options={options}
            onChange={(option) => {
              if (option && onChange) {
                onChange(option.value);
              }
            }}
            menuPlacement="auto"
            menuShouldScrollIntoView={false}
            value={selectedOption}
            placeholder={placeholder}
            classNamePrefix="styled-select"
            isDisabled={isDisabled}
            onKeyDown={handleTypeaheadKeyDown}
          />
        </SelectWrapper>
        {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
      </InputWrapper>
    );
  }
  const selectedOption = options.find(
    (option) => option.value === selectedValue,
  );
  return (
    <InputWrapper data-testid={dataTestid}>
      <SelectWrapper selectStyles={styles ?? {}}>
        <StyledSelect
          {...(backgroundColor ? { bgColor: backgroundColor } : {})}
          hasError={!!errorMessage}
          ref={
            selectInstanceRef as MutableRefObject<SelectInstance<DropDownOptionNoIcon> | null>
          }
          styles={{
            control: composeStyles,
            menu: composeStyles,
            menuList: composeStyles,
            valueContainer: composeStyles,
            singleValue: composeStyles,
          }}
          isSearchable={false}
          options={options}
          onChange={(option) => {
            if (option && onChange) {
              onChange(option.value);
            }
          }}
          menuPlacement="auto"
          menuShouldScrollIntoView={false}
          data-testid={dataTestid}
          value={selectedOption ?? null}
          placeholder={placeholder}
          classNamePrefix="styled-select"
          isDisabled={isDisabled}
          onKeyDown={handleTypeaheadKeyDown}
        />
      </SelectWrapper>
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </InputWrapper>
  );
};

export default Dropdown;

const IconOption: React.FC<
  OptionProps<DropDownOptionWithIcon | undefined, false>
> = (props) => {
  const { data } = props;
  const { label, Icon } = data ?? {};

  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {Icon && (
          <Icon
            style={{ width: 24, boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)" }}
          />
        )}
        <span>{label}</span>
      </div>
    </components.Option>
  );
};

const IconSingleValue: React.FC<
  SingleValueProps<DropDownOptionWithIcon | undefined, false>
> = (props) => {
  const { data } = props;
  const { label, Icon } = data ?? {};

  return (
    <components.SingleValue {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {Icon && (
          <Icon
            style={{
              width: 24,
              marginTop: "-3px",
              boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)",
            }}
          />
        )}
        <span>{label}</span>
      </div>
    </components.SingleValue>
  );
};
